import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Container,
  Avatar,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Save,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  Login,
  Logout,
  Lock,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface ExperienciaData {
  id?: number;
  meu_humor: number | null;
  experiencia_positiva: string;
  experiencia_negativa: string;
  data_experiencia: string;
}

interface Cliente {
  id: number;
  nome: string;
  celular: string;
}

interface LoginData {
  token: string;
  ultimosDigitos: string;
}


const MinhaExperiencia: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // Estados de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loginData, setLoginData] = useState<LoginData>({ token: '', ultimosDigitos: '' });
  const [clienteData, setClienteData] = useState<Cliente | null>(null);
  const [unauthorizedDialog, setUnauthorizedDialog] = useState(false);

  // Estados do formulário
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [successDialog, setSuccessDialog] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(4);
  const [experiencia, setExperiencia] = useState<ExperienciaData>({
    meu_humor: null,
    experiencia_positiva: '',
    experiencia_negativa: '',
    data_experiencia: new Date().toISOString().split('T')[0]
  });

  const humorOptions = [
    { value: 1, icon: SentimentVeryDissatisfied, label: 'Muito Triste', color: '#f44336' },
    { value: 2, icon: SentimentDissatisfied, label: 'Triste', color: '#ff9800' },
    { value: 3, icon: SentimentNeutral, label: 'Neutro', color: '#9e9e9e' },
    { value: 4, icon: SentimentSatisfied, label: 'Feliz', color: '#4caf50' },
    { value: 5, icon: SentimentVerySatisfied, label: 'Muito Feliz', color: '#2196f3' }
  ];

  useEffect(() => {
    if (!token) {
      setError('Token não fornecido');
      return;
    }
    setLoginData(prev => ({ ...prev, token }));
  }, [token]);

  // Função de login
  const handleLogin = async () => {
    if (!loginData.ultimosDigitos) {
      setError('Por favor, digite os últimos 4 dígitos do seu celular');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/experiencia/validar-acesso', {
        token: loginData.token,
        ultimosDigitos: loginData.ultimosDigitos,
      });

      const data = response.data;

      if (response.status === 200 && data.success) {
        setClienteData({
          id: data.clienteId,
          nome: data.nomeCliente,
          celular: loginData.ultimosDigitos
        });
        setIsAuthenticated(true);
        setError('');
      } else {
        if (data.limiteAtingido) {
          setError('Limite diário atingido! Você já registrou 3 experiências hoje. Tente novamente amanhã.');
        } else {
          setError(data.message || 'Erro ao fazer login');
        }
      }
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    if (!experiencia.meu_humor) {
      setError('Por favor, selecione seu humor atual');
      return;
    }

    // Mapear números para strings esperadas pelo backend
    const humorMap: { [key: number]: string } = {
      1: 'muito_triste',
      2: 'triste', 
      3: 'neutro',
      4: 'feliz',
      5: 'muito_feliz'
    };

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/api/experiencia/salvar/${token}`, {
        meu_humor: humorMap[experiencia.meu_humor],
        experiencia_positiva: experiencia.experiencia_positiva,
        experiencia_negativa: experiencia.experiencia_negativa,
      });

      const data = response.data;

      if (response.status === 200) {
        // Reset dos dados após salvar
        setExperiencia({
          meu_humor: null,
          experiencia_positiva: '',
          experiencia_negativa: '',
          data_experiencia: new Date().toISOString().split('T')[0]
        });
        
        // Exibir pop-up de sucesso
        setSuccessDialog(true);
      } else {
        setError(data.error || 'Erro ao salvar experiência');
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Erro ao salvar experiência. Tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  // useEffect para logout automático
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let countdownTimer: NodeJS.Timeout;
    
    if (successDialog) {
      // Timer para o countdown
      countdownTimer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Timer para logout automático
      timer = setTimeout(() => {
        handleLogout();
        setSuccessDialog(false);
        setCountdown(4); // Reset countdown
      }, 4000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [successDialog]);

  // Função de logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setClienteData(null);
    setLoginData(prev => ({ ...prev, ultimosDigitos: '' }));
    setExperiencia({
      meu_humor: null,
      experiencia_positiva: '',
      experiencia_negativa: '',
      data_experiencia: new Date().toISOString().split('T')[0]
    });
    setError('');
    setSuccess('');
  };

  // Função para confirmar sucesso e fazer logout
  const handleSuccessConfirm = () => {
    setSuccessDialog(false);
    setCountdown(4); // Reset countdown
    handleLogout();
  };

  const handleHumorChange = (valor: number) => {
    setExperiencia(prev => ({ ...prev, meu_humor: valor }));
    setError('');
  };

  const formatarDataHora = () => {
    const agora = new Date();
    return agora.toLocaleString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para mudança nos campos de login
  const handleLoginChange = (field: keyof LoginData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError(''); // Limpar erro ao digitar
  };

  // Tela de login - Layout igual ao MeuCadastro
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box textAlign="center" mb={4}>
            <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: 'primary.main', width: 56, height: 56 }}>
              <Lock />
            </Avatar>
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Minha Experiência
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Acesse para registrar sua experiência
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
            <TextField
              fullWidth
              label="Token de Acesso"
              value={loginData.token}
              onChange={handleLoginChange('token')}
              variant="outlined"
              disabled={true}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Senha de Acesso"
              type={showPassword ? 'text' : 'password'}
              value={loginData.ultimosDigitos}
              onChange={handleLoginChange('ultimosDigitos')}
              variant="outlined"
              inputProps={{ maxLength: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Acessar'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Tela principal (após login)
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 2 }
      }}
    >
      <Box maxWidth="md" mx="auto">
        <Card elevation={3}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box textAlign="center" mb={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4" component="h1" color="primary">
                  Minha Experiência
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Logout />}
                  onClick={handleLogout}
                >
                  Sair
                </Button>
              </Box>
              
              {clienteData && (
                <>
                  <Typography variant="h6" color="text.secondary">
                    Olá, {clienteData.nome}!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {formatarDataHora()}
                  </Typography>
                </>
              )}
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Alertas */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Grid container spacing={4}>
              {/* Seleção de Humor */}
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Como está seu humor hoje?
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="space-around"
                    flexWrap="wrap"
                    gap={2}
                    mt={2}
                  >
                    {humorOptions.map((option) => {
                      const IconComponent = option.icon;
                      const isSelected = experiencia.meu_humor === option.value;
                      
                      return (
                        <Box
                          key={option.value}
                          textAlign="center"
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleHumorChange(option.value)}
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '50%',
                              border: isSelected ? '3px solid' : '2px solid transparent',
                              borderColor: isSelected ? option.color : 'transparent',
                              backgroundColor: isSelected ? `${option.color}20` : 'transparent',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: `${option.color}10`,
                                transform: 'scale(1.1)'
                              }
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: { xs: 40, md: 50 },
                                color: option.color
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            display="block"
                            mt={1}
                            fontWeight={isSelected ? 'bold' : 'normal'}
                            color={isSelected ? option.color : 'text.secondary'}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>

              {/* Experiência Positiva */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Experiência Positiva do Dia
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="Conte sobre algo bom que aconteceu hoje..."
                    value={experiencia.experiencia_positiva}
                    onChange={(e) => setExperiencia(prev => ({
                      ...prev,
                      experiencia_positiva: e.target.value
                    }))}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#4caf50'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4caf50'
                        }
                      }
                    }}
                  />
                </Paper>
              </Grid>

              {/* Experiência Negativa */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Experiência Negativa do Dia
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    placeholder="Conte sobre algo que não foi tão bom hoje..."
                    value={experiencia.experiencia_negativa}
                    onChange={(e) => setExperiencia(prev => ({
                      ...prev,
                      experiencia_negativa: e.target.value
                    }))}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#f44336'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f44336'
                        }
                      }
                    }}
                  />
                </Paper>
              </Grid>

              {/* Botão Salvar */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    onClick={handleSalvar}
                    disabled={saving || !experiencia.meu_humor}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      minWidth: 200
                    }}
                  >
                    {saving ? 'Salvando...' : 'Salvar Experiência'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Dialog de Sucesso */}
      <Dialog
        open={successDialog}
        onClose={() => {}} // Mantém bloqueado
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
          Experiência Salva!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            Sua experiência foi registrada com sucesso!
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            Você será deslogado automaticamente em {countdown} segundos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleSuccessConfirm}
            variant="contained"
            color="success"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MinhaExperiencia;